/**
 * Booking Model for V2 Services
 */

export type BookingStatus =
  | 'draft'
  | 'payment_pending'
  | 'payment_confirmed'
  | 'scheduled'
  | 'in_progress'
  | 'completed'
  | 'cancelled'
  | 'failed';

export interface BookingSchedule {
  scheduledDate?: string;
  timeSlotLabel?: string;
  buildingId?: string;
  floor?: number;
  apartmentId?: string;
  notes?: string;
  confirmedAt?: Date;
}

export interface StatusEntry {
  status: BookingStatus;
  timestamp: Date;
  actor: string;
  notes?: string;
}

export interface BookingData {
  id?: string;
  userId: string;
  status: BookingStatus;
  serviceId: string;
  roomType: string;
  amount: number;
  currency: string;
  paymentIntentId?: string;
  robotId?: string;
  schedule?: BookingSchedule;
  statusHistory: StatusEntry[];
  metadata?: Record<string, any>;
  createdAt: Date;
  updatedAt: Date;
}

export class Booking {
  public id?: string;
  public userId: string;
  public status: BookingStatus;
  public serviceId: string;
  public roomType: string;
  public amount: number;
  public currency: string;
  public paymentIntentId?: string;
  public robotId?: string;
  public schedule: BookingSchedule;
  public statusHistory: StatusEntry[];
  public metadata: Record<string, any>;
  public createdAt: Date;
  public updatedAt: Date;

  constructor(data: Partial<BookingData>) {
    this.id = data.id;
    this.userId = data.userId || '';
    this.status = data.status || 'draft';
    this.serviceId = data.serviceId || '';
    this.roomType = data.roomType || '';
    this.amount = data.amount || 0;
    this.currency = data.currency || 'USD';
    this.paymentIntentId = data.paymentIntentId;
    this.robotId = data.robotId;
    this.schedule = data.schedule || {};
    this.statusHistory = data.statusHistory || [];
    this.metadata = data.metadata || {};
    this.createdAt = data.createdAt || new Date();
    this.updatedAt = data.updatedAt || new Date();
  }

  public toFirestore(): Record<string, any> {
    return {
      userId: this.userId,
      status: this.status,
      serviceId: this.serviceId,
      roomType: this.roomType,
      amount: this.amount,
      currency: this.currency,
      paymentIntentId: this.paymentIntentId,
      robotId: this.robotId,
      schedule: this.schedule,
      statusHistory: this.statusHistory,
      metadata: this.metadata,
      createdAt: this.createdAt,
      updatedAt: this.updatedAt
    };
  }

  public static fromFirestore(data: any): Booking {
    return new Booking({
      ...data,
      createdAt: data.createdAt?.toDate?.() || data.createdAt,
      updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      statusHistory: data.statusHistory?.map((entry: any) => ({
        ...entry,
        timestamp: entry.timestamp?.toDate?.() || entry.timestamp
      })) || []
    });
  }

  public addStatusEntry(status: BookingStatus, actor: string, notes?: string): void {
    this.status = status;
    this.statusHistory.push({
      status,
      timestamp: new Date(),
      actor,
      notes
    });
    this.updatedAt = new Date();
  }

  public canTransitionTo(newStatus: BookingStatus): boolean {
    const transitions: Record<BookingStatus, BookingStatus[]> = {
      'draft': ['payment_pending', 'cancelled'],
      'payment_pending': ['payment_confirmed', 'cancelled', 'failed'],
      'payment_confirmed': ['scheduled', 'cancelled'],
      'scheduled': ['in_progress', 'cancelled'],
      'in_progress': ['completed', 'cancelled'],
      'completed': [],
      'cancelled': [],
      'failed': ['payment_pending']
    };

    return transitions[this.status]?.includes(newStatus) || false;
  }
}